import type { InputCaption, StringifyOptions } from "./types";
import { toMilliseconds, toSrtTimestamp, toVttTimestamp } from "./utils/time";

interface NormalizedCaption {
  startMs: number;
  endMs: number;
  text: string;
  settings?: string;
}

/**
 * Converts subtitle captions to SRT format string
 */
export const stringify = (captions: InputCaption[], options: StringifyOptions = {}): string => {
  const { format = "srt", includeBom = false } = options;

  if (format === "vtt") {
    return stringifyVtt(captions, options);
  }

  return stringifySrt(captions, { includeBom });
};

/**
 * Converts subtitle captions to SRT format string
 */
export const stringifySrt = (captions: InputCaption[], options: StringifyOptions = {}): string => {
  const { includeBom = false } = options;

  assertCaptionsArray(captions);
  const lines: string[] = [];

  captions.forEach((caption, index) => {
    appendCaptionLines(lines, index, normalizeCaption(caption, index), formatSrtTimestampLine);
  });

  const result = lines.join("\n");

  // Add BOM if requested (at the very beginning, no newline after)
  return includeBom ? `\uFEFF${result}` : result;
};

/**
 * Converts subtitle captions to WebVTT format string
 */
export const stringifyVtt = (captions: InputCaption[], options: StringifyOptions = {}): string => {
  const { includeBom = false, vttHeader = "WEBVTT" } = options;

  assertCaptionsArray(captions);
  const lines: string[] = [];

  // Add VTT header
  lines.push(vttHeader);
  lines.push("");

  captions.forEach((caption, index) => {
    appendCaptionLines(lines, index, normalizeCaption(caption, index), formatVttTimestampLine);
  });

  const result = lines.join("\n");

  // Add BOM if requested (at the very beginning, no newline after)
  return includeBom ? `\uFEFF${result}` : result;
};

const assertCaptionsArray = (captions: InputCaption[]): void => {
  if (!Array.isArray(captions)) {
    throw new Error("Captions must be an array");
  }
};

const normalizeCaption = (caption: InputCaption, index: number): NormalizedCaption => {
  if (!caption || typeof caption.text !== "string") {
    throw new Error(`Invalid caption at index ${index}: missing or invalid text`);
  }

  const startMs = normalizeTimestamp(caption.start);
  const endMs = normalizeTimestamp(caption.end);

  if (typeof startMs !== "number" || typeof endMs !== "number" || startMs < 0 || endMs < 0) {
    throw new Error(`Invalid caption at index ${index}: invalid start or end time`);
  }

  if (startMs >= endMs) {
    throw new Error(`Invalid caption at index ${index}: start time must be before end time`);
  }

  const normalized: NormalizedCaption = {
    endMs,
    startMs,
    text: caption.text,
  };

  if (caption.settings !== undefined) {
    normalized.settings = caption.settings;
  }

  return normalized;
};

const normalizeTimestamp = (timestamp: InputCaption["start"]): number => {
  return typeof timestamp === "string" ? toMilliseconds(timestamp) : timestamp;
};

const appendCaptionLines = (
  lines: string[],
  index: number,
  caption: NormalizedCaption,
  formatTimestampLine: (caption: NormalizedCaption) => string
): void => {
  lines.push((index + 1).toString());
  lines.push(formatTimestampLine(caption));
  lines.push(caption.text);
  lines.push("");
};

const formatSrtTimestampLine = ({ startMs, endMs }: NormalizedCaption): string => {
  return `${toSrtTimestamp(startMs)} --> ${toSrtTimestamp(endMs)}`;
};

const formatVttTimestampLine = ({ startMs, endMs, settings }: NormalizedCaption): string => {
  const timestampLine = `${toVttTimestamp(startMs)} --> ${toVttTimestamp(endMs)}`;
  return settings ? `${timestampLine} ${settings}` : timestampLine;
};
